import { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { NegotiationDetail, NestedLegItem } from 'api'
import { Locale, Translation } from 'locale'
import { getNegotiatedValue } from 'model'
import { swallowPrecisionError } from 'utils/Decimal'
import {
  canEditIncomingNegotiationPreAssignmentStartConsiderationOverride,
  canEditNegotiationPreAssignmentNotional,
  canEditNegotiationPreAssignmentStartConsideration,
  getNegotiationDetailFromAgGridContext,
  haircutTypeFormatter,
  makeHaircutFormatter,
  makeRateFormatter
} from 'view/shared/datagrid'
import {
  DetailGridNegotiableCellRendererProps,
  detailGridNegotiableCellRendererRowDataGetter,
  makePreAssignmentAccruedInterestColumn,
  makePreAssignmentAllInPriceColumn,
  makePreAssignmentEndConsiderationColumn,
  makePreAssignmentHaircutColumn,
  makePreAssignmentHaircutTypeColumn,
  makePreAssignmentMyEntityColumn,
  makePreAssignmentNotionalColumn,
  makePreAssignmentRateColumn,
  makePreAssignmentRepoInterestColumn,
  makePreAssignmentStartConsiderationColumn,
  makePreAssignmentStartConsiderationOverrideColumn
} from 'view/shared/preAssignments'
import { NegotiableCellRenderer } from '../LiveNegotiationsGrid/NegotiableCellRenderer'
import {
  getNotionalModificationType,
  getStartConsiderationModificationType,
  getStartConsiderationOverrideModificationType
} from '../shared/deltaGetters'

export const incomingPreAssignmentsSubGridColumnDefs: ColDef<NestedLegItem>[] = [
  makePreAssignmentNotionalColumn({
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      rowDataGetter: detailGridNegotiableCellRendererRowDataGetter,
      getModificationType: ({ data }: DetailGridNegotiableCellRendererProps<NestedLegItem>) => {
        return getNotionalModificationType(data?.negotiationFields?.NOTIONAL?.negotiableFieldRequired)
      }
    },
    editable: canEditNegotiationPreAssignmentNotional
  }),
  makePreAssignmentMyEntityColumn({ headerName: Locale.translate(Translation.TC_COUNTERPARTY) }),
  makePreAssignmentStartConsiderationColumn({
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      rowDataGetter: detailGridNegotiableCellRendererRowDataGetter,
      getModificationType: swallowPrecisionError(({ data }: DetailGridNegotiableCellRendererProps<NestedLegItem>) => {
        return getStartConsiderationModificationType(data?.negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired)
      })
    },
    editable: canEditNegotiationPreAssignmentStartConsideration
  }),
  makePreAssignmentStartConsiderationOverrideColumn({
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      rowDataGetter: detailGridNegotiableCellRendererRowDataGetter,
      getModificationType: swallowPrecisionError(({ data }: DetailGridNegotiableCellRendererProps<NestedLegItem>) => {
        return getStartConsiderationOverrideModificationType(data?.negotiationFields?.START_CONSIDERATION_OVERRIDE?.negotiableFieldRequired)
      })
    },
    editable: canEditIncomingNegotiationPreAssignmentStartConsiderationOverride
  }),
  makePreAssignmentEndConsiderationColumn(),
  makePreAssignmentRateColumn({
    valueGetter: (params: ValueGetterParams<NestedLegItem>) => {
      const negotiationDetail = getNegotiationDetailFromAgGridContext(params)
      return getNegotiatedValue(negotiationDetail?.negotiation?.rate)
    },
    valueFormatter: (params: ValueFormatterParams<NestedLegItem>) => {
      const negotiationDetail = getNegotiationDetailFromAgGridContext(params)
      return makeRateFormatter()({ ...params, data: negotiationDetail })
    }
  }),
  makePreAssignmentHaircutColumn({
    valueGetter: params => {
      const negotiationDetail = params.context?.negotiationDetail as NegotiationDetail | null
      return getNegotiatedValue(negotiationDetail?.negotiation?.haircut)
    },
    valueFormatter: params => {
      const negotiationDetail = params.context?.negotiationDetail as NegotiationDetail | null

      return makeHaircutFormatter()({
        ...params,
        data: { ...negotiationDetail }
      })
    }
  }),
  makePreAssignmentHaircutTypeColumn({
    valueGetter: params => {
      const negotiationDetail = params.context?.negotiationDetail as NegotiationDetail | null
      return getNegotiatedValue(negotiationDetail?.negotiation?.haircutType)
    },
    valueFormatter: haircutTypeFormatter
  }),
  makePreAssignmentAccruedInterestColumn(),
  makePreAssignmentRepoInterestColumn(),
  makePreAssignmentAllInPriceColumn()
]
